// The ONE table cell for a named resource: shows the human label alone and
// reveals a click-to-copy short id only on cell hover or copy-button focus, so
// ids stay a copy affordance instead of cluttering rows as persistent chips
// (the `.marker-hover-copy` pattern). `stopPropagation` keeps the copy click
// from triggering row navigation.
//
// - `name` + `id`: label leads, short id reveals on hover/focus.
// - `id` only (name null/undefined, e.g. an unresolved lookup): the short id is
//   itself the visible, click-to-copy content — never doubled up as label AND
//   reveal.
// - `name` only (no id): the label alone.
import type { ReactNode } from "react";

import { shortResourceId } from "../../lib/shellNavigation";
import { Copyable, Text } from "../../ui";

export interface NameWithIdProps {
  name: ReactNode;
  id: string | null | undefined;
  idLabel: string;
  strong?: boolean;
  /** Fade a long name so it cannot widen its column off-screen. The full text
   *  stays reachable as the native title when `name` is plain text. */
  truncate?: boolean;
}

export function NameWithId({
  name,
  id,
  idLabel,
  strong = false,
  truncate = true,
}: NameWithIdProps) {
  const label = strong && name != null ? <strong>{name}</strong> : name;
  // `strong` wraps the name in an element, and Text only auto-titles plain
  // string children — so the title is supplied here from the raw name.
  const title = typeof name === "string" ? name : undefined;
  // Fading without a recoverable full value would hide data, so a
  // non-string name (no title to fall back on) renders untruncated.
  const labelText =
    truncate && title !== undefined ? (
      <Text as="span" title={title} truncate>
        {label}
      </Text>
    ) : (
      <Text as="span">{label}</Text>
    );

  if (!id) {
    return labelText;
  }

  if (name == null) {
    return (
      <Copyable
        label={idLabel}
        value={id}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {shortResourceId(id)}
      </Copyable>
    );
  }

  return (
    <span className="marker-hover-copy">
      {labelText}
      <Copyable
        className="marker-hover-copy__reveal"
        label={idLabel}
        value={id}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {shortResourceId(id)}
      </Copyable>
    </span>
  );
}
